import React, { useState } from 'react';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram, ArrowUp } from 'lucide-react';
import { apiService } from '../services/api';
import { useApiCall } from '../hooks/useApi';
import useApi from '../hooks/useApi';
import { InlineSpinner } from './LoadingSpinner';

const Footer = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  
  const { execute: subscribeNewsletter, loading: subscribing, error: subscribeError } = useApiCall();
  const { data: contactInfo } = useApi(() => apiService.getContactInfo(), []);

  // Default contact info as fallback
  const defaultContactInfo = {
    email: "info@transpost.co",
    phone: "+1 (555) 123-4567",
    address: "Suite 4, Level 8, Time Square Building, Andheri - Kurla Rd, B Wing, Chimatpada, Marol, Andheri East, Mumbai, Maharashtra 400059"
  };

  const displayContactInfo = contactInfo || defaultContactInfo;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!newsletterEmail.trim()) return;
    
    try {
      const response = await subscribeNewsletter(() => apiService.subscribeNewsletter(newsletterEmail));
      setNewsletterMessage(response.message || 'Thank you for subscribing!');
      setNewsletterEmail('');
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setNewsletterMessage('');
      }, 3000);
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setNewsletterMessage('Failed to subscribe. Please try again.');
      setTimeout(() => {
        setNewsletterMessage('');
      }, 3000);
    }
  };

  return (
    <footer className="bg-gray-900 pt-10 text-white relative">
      {/* Main Footer Content */}
      <div className="container mx-auto px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-8 h-8 bg-gradient-to-br from-[#2aa996] to-[#1e7a6b] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">T</span>
              </div>
              <span className="text-2xl font-bold">Transpost</span>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Transforming businesses through innovative technology solutions.
              We specialize in application development, cybersecurity, and AI
              transformation.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.linkedin.com/company/79335731/admin/dashboard/
"
                className="bg-gray-800 p-3 rounded-lg hover:bg-[#2aa996] transition-all duration-300 transform hover:scale-110"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection("home")}
                  className="text-gray-400 hover:text-[#2aa996] transition-colors duration-300"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("about")}
                  className="text-gray-400 hover:text-[#2aa996] transition-colors duration-300"
                >
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("services")}
                  className="text-gray-400 hover:text-[#2aa996] transition-colors duration-300"
                >
                  Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("portfolio")}
                  className="text-gray-400 hover:text-[#2aa996] transition-colors duration-300"
                >
                  Portfolio
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("team")}
                  className="text-gray-400 hover:text-[#2aa996] transition-colors duration-300"
                >
                  Team
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xl font-bold mb-6">Our Services</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="#"
                  className="text-gray-400 hover:text-[#2aa996] transition-colors duration-300"
                >
                  Application Development
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-400 hover:text-[#2aa996] transition-colors duration-300"
                >
                  Cybersecurity Solutions
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-400 hover:text-[#2aa996] transition-colors duration-300"
                >
                  AI Transformation
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-400 hover:text-[#2aa996] transition-colors duration-300"
                >
                  Cloud Migration
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-400 hover:text-[#2aa996] transition-colors duration-300"
                >
                  Digital Transformation
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold mb-6">Contact Info</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Mail className="text-[#2aa996] mt-1 flex-shrink-0" size={16} />
                <div>
                  <p className="text-gray-400">{displayContactInfo.email}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <MapPin
                  className="text-[#2aa996] mt-1 flex-shrink-0"
                  size={16}
                />
                <div>
                  <p className="text-gray-400">{displayContactInfo.address}</p>
                </div>
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="mt-6">
              <h4 className="font-semibold mb-2">Stay Updated</h4>

              {newsletterMessage ? (
                <div
                  className={`p-3 rounded-lg text-sm ${
                    subscribeError
                      ? "bg-red-100 text-red-800"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {newsletterMessage}
                </div>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="flex">
                  <input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    disabled={subscribing}
                    className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-l-lg focus:outline-none focus:border-[#2aa996] text-white disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={subscribing || !newsletterEmail.trim()}
                    className="bg-[#2aa996] px-4 py-2 rounded-r-lg hover:bg-[#1e7a6b] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {subscribing ? <InlineSpinner size="sm" /> : "Subscribe"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t mt-1 py-4 border-gray-800">
        <div className="container mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © 2024 Transpost. All rights reserved. | Privacy Policy | Terms of
              Service
            </div>
            <div className="flex items-center space-x-6">
              <span className="text-gray-400 text-sm">
                Made with ❤️ by Transpost Team
              </span>
            
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-32 h-32 bg-[#2aa996] opacity-5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-40 h-40 bg-[#2aa996] opacity-5 rounded-full blur-3xl"></div>
    </footer>
  );
};

export default Footer;
